<template>
  <main-template>
    <div class="etapa-layout">
      <!-- Header fixo -->
      <div class="etapa-header">
        <v-row v-if="loading">
          <v-col><v-progress-linear indeterminate color="primary" /></v-col>
        </v-row>
        <template v-else>
          <div class="d-flex justify-space-between align-center">
            <div>
              <h1 class="mb-0" style="line-height: 1.2; font-size: 1.5rem">
                {{ etapa.descricao }}
              </h1>
            </div>
            <v-btn outlined color="primary" @click="configurarEtapa">
              <v-icon left small>fa fa-cog</v-icon>
              Configurar etapa
            </v-btn>
          </div>

          <v-row class="mt-3 mb-0">
            <v-col cols="12" sm="4">
              <v-card outlined class="rounded-xl pa-4">
                <div class="d-flex justify-space-between align-center">
                  <div>
                    <div class="text-overline grey--text">MÓDULOS</div>
                    <div class="text-h4 font-weight-bold">
                      {{ etapa.modulos ? etapa.modulos.length : 0 }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ concluidosCount }} concluídos · {{ emProgressoCount }} em progresso
                    </div>
                  </div>
                  <v-btn icon small style="background: #e3f2fd">
                    <v-icon color="blue darken-2" small>fa fa-th</v-icon>
                  </v-btn>
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="4">
              <v-card outlined class="rounded-xl pa-4">
                <div class="d-flex justify-space-between align-center">
                  <div>
                    <div class="text-overline grey--text">CAMPOS CONFIGURADOS</div>
                    <div class="text-h4 font-weight-bold">{{ totalCampos }}</div>
                    <div class="text-caption grey--text">Distribuídos entre os módulos</div>
                  </div>
                  <v-btn icon small style="background: #e8f5e9">
                    <v-icon color="green darken-2" small>fa fa-list</v-icon>
                  </v-btn>
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" sm="4">
              <v-card outlined class="rounded-xl pa-4">
                <div class="d-flex justify-space-between align-center">
                  <div>
                    <div class="text-overline grey--text">VALIDAÇÕES OK</div>
                    <div class="text-h4 font-weight-bold">100%</div>
                    <div class="text-caption grey--text">Regras de consistência</div>
                  </div>
                  <v-btn icon small style="background: #e8f5e9">
                    <v-icon color="green darken-2" small>fa fa-check-circle</v-icon>
                  </v-btn>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <div class="text-overline font-weight-bold mt-4 mb-1 grey--text text--darken-1">
            MÓDULOS
          </div>
        </template>
      </div>

      <!-- Conteúdo scrollável -->
      <div v-if="!loading" class="etapa-scroll">
        <v-expansion-panels v-if="etapa.modulos && etapa.modulos.length > 0" flat>
          <v-expansion-panel
            v-for="modulo in etapa.modulos"
            :key="modulo.id"
            class="mb-3 rounded-lg"
            style="border: 1px solid #e0e0e0"
          >
            <v-expansion-panel-header>
              <div class="d-flex align-center" style="gap: 12px">
                <div
                  class="d-flex align-center justify-center rounded-lg"
                  style="
                    width: 48px;
                    height: 48px;
                    background: #f5f5f5;
                    font-weight: bold;
                    font-size: 14px;
                    color: #555;
                    flex-shrink: 0;
                  "
                >
                  {{ modulo.codigo }}
                </div>
                <div class="flex-grow-1">
                  <div class="font-weight-medium">{{ modulo.descricao }}</div>
                  <div class="text-caption grey--text">
                    {{ modulo.campos ? modulo.campos.length : 0 }} campos
                    <template v-if="modulo.updated_at">
                      · última edição {{ formatarData(modulo.updated_at) }}
                    </template>
                  </div>
                </div>
                <v-btn x-small color="primary" class="mr-2" @click.stop="abrirDialogCampo(modulo)">
                  <v-icon x-small left>fa fa-plus</v-icon>
                  Adicionar campo
                </v-btn>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-list v-if="modulo.campos && modulo.campos.length > 0" dense>
                <v-list-item v-for="campo in modulo.campos" :key="campo.id">
                  <v-list-item-content>
                    <v-list-item-title>{{ campo.nome }}</v-list-item-title>
                    <v-list-item-subtitle>{{ campo.placeholder || "—" }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action class="d-flex flex-row align-center" style="gap: 4px">
                    <v-chip x-small label outlined color="grey darken-1">{{ campo.tipo }}</v-chip>
                    <v-btn icon x-small @click="editarCampo(campo, modulo)">
                      <v-icon x-small>fa fa-edit</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      color="red lighten-2"
                      @click="confirmarExcluirCampo(campo, modulo)"
                    >
                      <v-icon x-small>fa fa-trash</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
              <div v-else class="text-caption grey--text py-2">
                Nenhum campo cadastrado neste módulo.
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <div v-else class="text-caption grey--text">Nenhum módulo cadastrado nesta etapa.</div>
      </div>
    </div>

    <!-- Dialog criar/editar campo -->
    <v-dialog v-model="dialogCampo" max-width="500" @click:outside="fecharDialog">
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">
          {{ formCampo.id ? "Editar campo" : "Novo campo" }}
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-text-field
            v-model="formCampo.nome"
            label="Nome"
            outlined
            dense
            :error-messages="erros.nome"
          />
          <v-select
            v-model="formCampo.tipo"
            :items="tiposCampo"
            label="Tipo"
            outlined
            dense
            :error-messages="erros.tipo"
          />
          <v-text-field
            v-model="formCampo.placeholder"
            label="Placeholder"
            outlined
            dense
            :error-messages="erros.placeholder"
          />
          <v-text-field
            v-model.number="formCampo.ordem"
            label="Ordem"
            outlined
            dense
            type="number"
            :error-messages="erros.ordem"
          />
          <div v-if="formCampo.tipo === 'select'">
            <div class="text-caption grey--text mb-1">Opções do select</div>
            <div
              v-for="(opcao, i) in formCampo.opcoes"
              :key="i"
              class="d-flex align-center mb-1"
              style="gap: 8px"
            >
              <v-text-field v-model="formCampo.opcoes[i]" dense outlined hide-details />
              <v-btn icon x-small color="red lighten-2" @click="removerOpcao(i)">
                <v-icon x-small>fa fa-times</v-icon>
              </v-btn>
            </div>
            <v-btn x-small text color="primary" @click="adicionarOpcao">
              <v-icon x-small left>fa fa-plus</v-icon>
              Adicionar opção
            </v-btn>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="fecharDialog">Cancelar</v-btn>
          <v-btn color="primary" :loading="salvando" @click="salvarCampo">Salvar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog confirmar exclusão -->
    <v-dialog v-model="dialogExcluir" max-width="400">
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">Excluir campo</v-card-title>
        <v-card-text
          >Deseja remover o campo
          <strong>{{ campoParaExcluir ? campoParaExcluir.nome : "" }}</strong
          >?</v-card-text
        >
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogExcluir = false">Cancelar</v-btn>
          <v-btn color="red" dark :loading="excluindo" @click="excluirCampo">Excluir</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </main-template>
</template>

<script>
const FORM_VAZIO = () => ({
  id: null,
  etapa_modulo_id: null,
  nome: "",
  tipo: "text",
  placeholder: "",
  ordem: 0,
  opcoes: [],
});

export default {
  data() {
    return {
      etapa: {},
      loading: true,
      filtroModulos: "todos",
      tiposCampo: ["text", "number", "select", "checkbox", "date", "textarea"],
      dialogCampo: false,
      formCampo: FORM_VAZIO(),
      moduloAtivo: null,
      erros: {},
      salvando: false,
      dialogExcluir: false,
      campoParaExcluir: null,
      moduloCampoParaExcluir: null,
      excluindo: false,
    };
  },

  computed: {
    etapaNumero() {
      if (!this.etapa.censo || !this.etapa.censo.etapas) return "—";
      const idx = this.etapa.censo.etapas.findIndex((e) => e.id === this.etapa.id);
      return idx >= 0 ? idx + 1 : "—";
    },

    totalCampos() {
      if (!this.etapa.modulos) return 0;
      return this.etapa.modulos.reduce((acc, m) => acc + (m.campos ? m.campos.length : 0), 0);
    },

    concluidosCount() {
      return this.etapa.modulos ? this.etapa.modulos.length : 0;
    },

    emProgressoCount() {
      return 0;
    },
  },

  async created() {
    await this.buscarEtapa();
  },

  methods: {
    goBack() {
      const censoId = this.etapa.censo_id || (this.etapa.censo && this.etapa.censo.id);
      if (censoId) {
        this.$router.push({ name: "censo-escolar-show", params: { id: censoId } });
      } else {
        this.$router.push({ name: "censo-escolar" });
      }
    },

    configurarEtapa() {},

    abrirDialogCampo(modulo) {
      this.moduloAtivo = modulo;
      this.formCampo = FORM_VAZIO();
      this.formCampo.etapa_modulo_id = modulo.id;
      this.erros = {};
      this.dialogCampo = true;
    },

    editarCampo(campo, modulo) {
      this.moduloAtivo = modulo;
      this.formCampo = {
        id: campo.id,
        etapa_modulo_id: modulo.id,
        nome: campo.nome,
        tipo: campo.tipo,
        placeholder: campo.placeholder || "",
        ordem: campo.ordem,
        opcoes: campo.opcoes ? [...campo.opcoes] : [],
      };
      this.erros = {};
      this.dialogCampo = true;
    },

    fecharDialog() {
      this.dialogCampo = false;
      this.formCampo = FORM_VAZIO();
      this.erros = {};
    },

    adicionarOpcao() {
      this.formCampo.opcoes.push("");
    },

    removerOpcao(i) {
      this.formCampo.opcoes.splice(i, 1);
    },

    async salvarCampo() {
      this.salvando = true;
      this.erros = {};
      try {
        const payload = { ...this.formCampo };
        if (payload.tipo !== "select") payload.opcoes = [];

        let campoSalvo;
        if (payload.id) {
          campoSalvo = await this.$services.censoService.updateCampo(payload.id, payload);
          const campos = this.moduloAtivo.campos;
          const idx = campos.findIndex((c) => c.id === campoSalvo.id);
          if (idx >= 0) this.$set(campos, idx, campoSalvo);
        } else {
          campoSalvo = await this.$services.censoService.storeCampo(payload);
          if (!this.moduloAtivo.campos) this.$set(this.moduloAtivo, "campos", []);
          this.moduloAtivo.campos.push(campoSalvo);
        }
        this.fecharDialog();
      } catch (e) {
        if (e.response && e.response.data && e.response.data.errors) {
          this.erros = e.response.data.errors;
        } else {
          this.$toast.error("Erro ao salvar campo.");
        }
      } finally {
        this.salvando = false;
      }
    },

    confirmarExcluirCampo(campo, modulo) {
      this.campoParaExcluir = campo;
      this.moduloCampoParaExcluir = modulo;
      this.dialogExcluir = true;
    },

    async excluirCampo() {
      this.excluindo = true;
      try {
        await this.$services.censoService.destroyCampo(this.campoParaExcluir.id);
        const campos = this.moduloCampoParaExcluir.campos;
        const idx = campos.findIndex((c) => c.id === this.campoParaExcluir.id);
        if (idx >= 0) campos.splice(idx, 1);
        this.dialogExcluir = false;
      } catch {
        this.$toast.error("Erro ao excluir campo.");
      } finally {
        this.excluindo = false;
      }
    },

    formatarData(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1)
        .toString()
        .padStart(2, "0")}`;
    },

    async buscarEtapa() {
      try {
        this.etapa = await this.$services.censoService.showEtapa(this.$route.params.etapaId);
      } catch (error) {
        this.$toast.danger("Erro ao carregar etapa.");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.etapa-layout {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 120px);
}

.etapa-header {
  flex-shrink: 0;
}

.etapa-scroll {
  flex: 1;
  overflow-y: auto;
  padding-right: 4px;
}
</style>
